import { Suspense, useState } from "react";
import styled from "styled-components/macro";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import SidePanel, { SidePanelTrigger } from "./SidePanel";
import { useSkipioApi, touchSkipioApi } from "../api";
import {
  BoxHeader,
  BoxTitle,
  BoxContent,
  BoxNavRight,
  BoxNavButton,
  BoxNavLeft,
  BoxFooter,
  Box,
} from "./Box";
import Button from "./Button";
import { Close } from "./Icons";
import ContactsWell from "./ContactsWell";
import Composer, { TMessage } from "./Composer";
import Drawer from "./Drawer";
import { SmsMessage, VoicemailMessage } from "./Message";
import { useFields } from "../hooks/use-fields";
import useDebounce from "../hooks/use-debounce";
import { useToast } from "../hooks/use-toast";
import { THEME_COLOR, WARNING_COLOR } from "../vars";
import usePlan from "../hooks/use-plan";
import { lighten, darken } from "polished";
import { Label } from "./Input";
import Stack from "./Stack";
import useExitWarning from "../hooks/use-prevent-exit";
import useMoment from "../hooks/use-moment";
import MessageLinterRule, { TLintRule, lintMessage } from "./MessageLinter";
import {
  ConfimContentSection,
  Emoji,
} from "./KeywordManagement/MsgKeywordSidePanel";
import Overlay from "./Overlay";
import { useSkipioQueries, useSkipioQuery } from "../hooks/use-query";
import { fetchUser } from "../api/users";
import { fetchMessage } from "../api/messages";

type TypMessage = {
  id?: string;
  contactId?: string;
  messageType?: string;
  deliverAt?: string | null;
  deliveredAt?: string | null;
  failedAt?: string | null;
  direction?: string;

  media?: any[];
  mediaUrls?: any[];
  mediaMimeTypes?: any[];

  human?: boolean;
  failedReason?: string | null;
  body?: string;
  time?: string;
  voicemailId?: string | null;
  campaignId?: string | null;
};

type SharedProps = {
  message?: TypMessage;
  contactIds?: string[];
  groupIds?: string[];
  keywordIds?: string[];
  messageId?: string | null;
};

function MessageComposerSidePanel({
  trigger,
  message = {},
  contactIds = [],
  groupIds = [],
  messageId = undefined,
}: {
  trigger: SidePanelTrigger;
} & SharedProps) {
  return (
    <SidePanel
      trigger={trigger}
      content={({ hide }) => (
        <MessageComposerBoxContent
          hide={hide}
          message={message}
          contactIds={contactIds}
          groupIds={groupIds}
          messageId={messageId}
        />
      )}
    />
  );
}

export function MessageComposerBoxContent({
  hide,
  message = {},
  messageId = null,
  contactIds = [],
  groupIds = [],
  keywordIds = [],
  onSendComplete = () => {},
  includeLinter = false,
}: {
  hide: () => void;
  onSendComplete?: () => void;
  includeLinter?: boolean;
} & SharedProps) {
  const moment = useMoment();
  const skipioApi = useSkipioApi();
  const queryClient = useQueryClient();
  const planLimits = usePlan();
  const user = useQuery({
    queryKey: ["users", "me"],
    queryFn: fetchUser,
    select: (queryData) => queryData.data,
  }).data!;
  const [successSend, setSuccessSend] = useState(false);
  const persistedMessage = useQuery({
    queryKey: ["messages", messageId, { skipRenderBody: true }],
    queryFn: () => fetchMessage(messageId!, { skipRenderBody: true }),
    select: (queryData) => queryData.data,
    enabled: !!messageId,
  }).data!;
  message = messageId ? persistedMessage : message;

  const { field, value, reset, isChanged, checkpoint } = useFields({
    initialValue: {
      recipients: { contactIds, groupIds, keywordIds },
      message: {
        body: "",
        deliverAt: message ? message.time : undefined,
        messageType: "SmsMessage",
        imageUrl:
          message && message.media && message.media.length !== 0
            ? message.media[0].url
            : null,
        imageType:
          message && message.media && message.media.length !== 0
            ? message.media[0].type
            : null,
        ...message,
      },
    },
  });

  useExitWarning(isChanged);
  const messageKeywords = useSkipioQuery({ queryKey: ["messageKeywords"] })
    .data;

  const groupContactIds = useSkipioQueries({
    queries: value.recipients.groupIds.map((id) => {
      return {
        queryKey: ["groups", id],
        suspense: false,
        useErrorBoundary: true,
        enabled: !!value.recipients.groupIds,
      };
    }),
  }).map((query) => query.isSuccess && query.data.contactIds);

  const recipientContactIds = [
    ...(value.recipients.contactIds ? [value.recipients.contactIds] : []),
    ...(value.recipients.groupIds ? groupContactIds : []),
  ].reduce((acc, item) => acc.concat(item), []);

  const totalRecipients = new Set(recipientContactIds).size;

  const isOverLimit =
    !planLimits.canSendMoreScheduledMessages ||
    // can't send while plan is paused
    planLimits.features.paused;

  const isOverRecipientLimit =
    Boolean(planLimits.maxContactsInScheduledMessage) &&
    totalRecipients > planLimits.maxContactsInScheduledMessage;

  const isTooLong =
    !!value.message.body && value.message.body.length > user.maxCharacters;

  const canSend =
    // Cant send if there's no recipient
    (value.recipients.contactIds.length + value.recipients.groupIds.length !==
      0 ||
      value.recipients.keywordIds.length > 0) &&
    // Can't send if theres no value
    ((!!value.message.body && value.message.body?.length > 0) ||
      value.message.imageUrl !== null ||
      value.message.voicemailId !== null) &&
    // Can't send while over limit
    !isOverLimit &&
    !isOverRecipientLimit &&
    // Can't send messages that are too long
    !isTooLong;

  const debouncedBody = useDebounce(value.message.body, 1000);

  const showToast = useToast();

  // Send a message
  const sendMessage = async (message?: TMessage, closeOverlay?: () => void) => {
    if (!canSend) return;

    const data =
      value.recipients.keywordIds.length === 0
        ? {
            recipients: [
              ...value.recipients.contactIds.map((id) => `contact-${id}`),
              ...value.recipients.groupIds.map((id) => `group-${id}`),
            ],
            message: {
              ...(value.message.voicemailId
                ? {
                    voicemailId: value.message.voicemailId,
                    messageType: "VoicemailMessage",
                  }
                : {
                    messageType: "SmsMessage",
                  }),
              body: value.message.body,
              mediaUrls: value.message.imageUrl
                ? [value.message.imageUrl]
                : undefined,
              mediaMimeTypes: value.message.imageUrl
                ? [value.message.imageType]
                : undefined,
              deliverAt: value.message.deliverAt
                ? new Date(value.message.deliverAt).toISOString()
                : undefined,
            },
          }
        : {
            scheduledMessage: {
              ...(value.message.voicemailId
                ? {
                    voicemailId: value.message.voicemailId,
                    messageType: "VoicemailMessage",
                  }
                : {
                    messageType: "SmsMessage",
                  }),
              body: value.message.body,
              mediaUrls: value.message.imageUrl
                ? [value.message.imageUrl]
                : undefined,
              mediaMimeTypes: value.message.imageUrl
                ? [value.message.imageType]
                : undefined,
              deliverAt: value.message.deliverAt
                ? new Date(value.message.deliverAt).toISOString()
                : null,
            },
          };

    const caller = messageId
      ? skipioApi.messages[messageId].put
      : skipioApi.messages.post;

    await caller(
      {},
      {
        data,
      }
    );

    await queryClient.invalidateQueries({ queryKey: ["messages"] });

    // re-fetch plan limits and message lists in the background
    touchSkipioApi("users/me", "calendarMessages");
    queryClient.invalidateQueries({ queryKey: ["users", "me"] });
    queryClient.invalidateQueries({ queryKey: ["messageThreads"] });
    queryClient.invalidateQueries({ queryKey: ["calendarMessages"] });

    if (hide) {
      reset();
      hide();
    } else {
      checkpoint();
    }

    if (data.message?.deliverAt)
      showToast("Message Scheduled", { success: true });
    else showToast("Message Sent", { success: true });
    onSendComplete();
  };

  async function deleteMessage() {
    await skipioApi.messages[messageId!].delete.post(
      {},
      {
        data: {
          recipients: contactIds.map((id) => `contact-${id}`),
        },
      }
    );

    hide();
    showToast("Message Deleted", { success: true });
    touchSkipioApi("users/me", "calendarMessages");
    queryClient.invalidateQueries({ queryKey: ["users", "me"] });
    queryClient.invalidateQueries({ queryKey: ["messageThreads"] });
    queryClient.invalidateQueries({ queryKey: ["calendarMessages"] });
    queryClient.invalidateQueries({ queryKey: ["messages"] });
  }

  const keywordArr = useSkipioQueries({
    queries: value.recipients.keywordIds.map((id) => {
      return { queryKey: ["keywords", id] };
    }),
  }).map((query) => query.data);

  let subscriberCount = 0;
  if (keywordArr.length > 0) {
    keywordArr.forEach((keyword) => {
      subscriberCount += keyword?.activeSubscriberCount || 0;
    });
  }

  let matchingLinterRules: TLintRule[] = [];
  if ((includeLinter || totalRecipients > 1) && !!value.message.body) {
    matchingLinterRules = lintMessage(value.message.body, {
      keywords: messageKeywords,
    });
  }

  return (
    <>
      <BoxHeader>
        <BoxTitle>New Message</BoxTitle>
        {Boolean(hide) && (
          <BoxNavRight>
            <BoxNavButton onClick={hide}>
              <Close />
            </BoxNavButton>
          </BoxNavRight>
        )}
      </BoxHeader>
      <BoxContent>
        <Stack>
          <>
            <Label>Recipients</Label>
            <ContactsWell
              {...field("recipients")}
              allowGroups
              canAdd={!message.campaignId}
              canRemove={!message.campaignId}
            />
          </>
          <>
            <Label>Message</Label>
            {/* @ts-expect-error */}
            <Composer {...field("message")} />
          </>
          <Center>
            {isOverLimit && (
              <Error>
                This message would send {totalRecipients} messages which is over
                your {planLimits.remainingScheduledMessages} remaining messages.
              </Error>
            )}
            {isOverRecipientLimit && (
              <Error>
                This message is to {totalRecipients} contacts which is over your
                limit of {planLimits.maxContactsInScheduledMessage}.
              </Error>
            )}
            {isTooLong && (
              <Error>
                This message exceeds the maximum character count of{" "}
                {user.maxCharacters}.
              </Error>
            )}
          </Center>
          {matchingLinterRules.length > 0 && (
            <Drawer
              header={
                <>
                  Message Mentor <span style={{ color: THEME_COLOR }}>™</span>
                </>
              }
            >
              {matchingLinterRules.map((rule, index) => (
                <MessageLinterRule rule={rule} key={index} />
              ))}
            </Drawer>
          )}
          {Boolean(
            recipientContactIds.length !== 0 &&
              (value.message.body?.trim() ||
                value.message.imageUrl ||
                value.message.voicemailId)
          ) && (
            <Drawer defaultOpen={true} header="Preview Messages">
              <Suspense fallback={null}>
                <Previewer
                  payload={{
                    body: debouncedBody,
                    voicemailId: value.message.voicemailId,
                    mediaUrls: value.message.imageUrl
                      ? [value.message.imageUrl]
                      : undefined,
                    mediaMimeTypes: value.message.imageUrl
                      ? [value.message.imageType]
                      : undefined,
                    deliverAt: value.message.deliverAt
                      ? new Date(value.message.deliverAt).toISOString()
                      : undefined,
                  }}
                  contactIds={recipientContactIds}
                  keywordIds={value.recipients.keywordIds}
                />
              </Suspense>
            </Drawer>
          )}
        </Stack>
      </BoxContent>
      <BoxFooter>
        <BoxNavLeft>
          {Boolean(messageId) && (
            <Button variant="destroy" onClick={deleteMessage}>
              Delete
            </Button>
          )}
        </BoxNavLeft>
        <BoxNavRight>
          <Button onClick={() => sendMessage()} disabled={!canSend}>
            {value.message.deliverAt
              ? `Send on ${moment(value.message.deliverAt).format("llll")}`
              : "Send"}
          </Button>
        </BoxNavRight>
      </BoxFooter>
      <Overlay
        isShowing={successSend}
        setShowing={setSuccessSend}
        onHide={() => {
          if (hide) {
            hide();
            reset();
          } else {
            checkpoint();
          }
          onSendComplete();
        }}
        content={({ hide }) => (
          <Box>
            <BoxHeader>
              <BoxTitle>
                {value.message.deliverAt
                  ? "Your Message is Scheduled"
                  : "Your Message is Queued for Sending"}
              </BoxTitle>
              <BoxNavRight>
                <BoxNavButton onClick={hide}>
                  <Close />
                </BoxNavButton>
              </BoxNavRight>
            </BoxHeader>
            <BoxContent>
              <ConfimContentSection center>
                <Stack>
                  {value.message.deliverAt ? (
                    <Emoji>&#9202;</Emoji>
                  ) : (
                    <Emoji>&#128077;</Emoji>
                  )}
                  <strong>Nice work!</strong>
                  <p>
                    Your message is queued and is preparing to send. It will
                    begin sending to the {subscriberCount} subscriber(s) in your{" "}
                    keyword group(s){" "}
                    <strong>
                      {keywordArr.map(
                        (keyword, index) =>
                          `${index > 0 ? ", " : ""}${keyword.keyword}`
                      )}
                    </strong>{" "}
                    {value.message.deliverAt
                      ? `at ${moment(value.message.deliverAt).format(
                          "LT"
                        )} on ${moment(value.message.deliverAt).format("LL")}`
                      : "when it's ready."}
                  </p>
                </Stack>
              </ConfimContentSection>
            </BoxContent>
          </Box>
        )}
        trigger={({ ref, show }) => <></>}
      />
    </>
  );
}

function ContactPreviewMessage({
  payload,
  contactId,
}: {
  payload: TypMessage;
  contactId: string;
}) {
  const messageType = payload.voicemailId ? "VoicemailMessage" : "SmsMessage";
  const moment = useMoment();
  const contact = useSkipioQuery({ queryKey: ["contacts", contactId] }).data;
  const { message } = useSkipioQuery({
    queryKey: [
      "messagePreviews",
      {
        contactId,
        messageType,
        ...payload,
      },
    ],
  }).data;

  const metadata = `To ${contact.fullName} on ${moment(
    payload.deliverAt
  ).format("lll")}`;

  if (payload.voicemailId) {
    return (
      <VoicemailMessage
        voicemailId={payload.voicemailId}
        key={contactId}
        metadata={metadata}
      />
    );
  } else {
    return (
      <SmsMessage key={contactId} metadata={metadata}>
        {message.body}
      </SmsMessage>
    );
  }
}

function KeywordPreviewMessage({
  payload,
  keywordId,
}: {
  payload: TypMessage;
  keywordId: string;
}) {
  const messageType = payload.voicemailId ? "VoicemailMessage" : "SmsMessage";
  const moment = useMoment();
  const keyword = useSkipioQuery({ queryKey: ["keywords", keywordId] }).data;
  const { message } = useSkipioQuery({
    queryKey: [
      "messagePreviews",
      {
        keywordId,
        messageType,
        ...payload,
      },
    ],
  }).data;

  const metadata = `To ${keyword.keyword} on ${moment(payload.deliverAt).format(
    "lll"
  )}`;

  if (payload.voicemailId) {
    return (
      <VoicemailMessage voicemailId={payload.voicemailId} metadata={metadata} />
    );
  } else {
    return (
      <SmsMessage key={keywordId} metadata={metadata}>
        {message.body}
      </SmsMessage>
    );
  }
}

function Previewer({
  payload,
  contactIds,
  keywordIds,
}: {
  payload: TypMessage;
  contactIds: string[];
  keywordIds: string[];
}) {
  return (
    <div style={{ padding: "20px" }}>
      {contactIds.slice(0, keywordIds.length > 0 ? 1 : 3).map((id) => (
        <ContactPreviewMessage payload={payload} contactId={id} />
      ))}

      {keywordIds.slice(0, 3).map((id) => (
        <KeywordPreviewMessage payload={payload} keywordId={id} />
      ))}
    </div>
  );
}

export default MessageComposerSidePanel;

const Center = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const Error = styled.div`
  border: 1px solid ${WARNING_COLOR};
  background: ${lighten(0.3, WARNING_COLOR)};
  color: ${darken(0.25, WARNING_COLOR)};
  padding: 10px 20px;
  border-radius: 3px;
  margin-bottom: 3em;
  font-size: 13px;
  width: 100%;
`;
